import React, { useState } from 'react';
import { Env, ErrorBanner, Spinner, SuccessBanner } from '../../shared';
import { useUserLookup } from '../api/useUserLookup';
import { useUserActions } from '../api/useUserActions';
import { UserGamification } from '../api/types';
import { CoinAdjustModal } from './CoinAdjustModal';
import { TransactionHistory } from './TransactionHistory';
import { FraudDetail } from '../components/FraudDetail';

const MIN_FLAG_REASON = 5;
const MIN_SEARCH_LEN = 2;

const SEARCH_TYPE_LABELS: Record<string, string> = {
  user_id: 'user id',
  phone_last4: 'phone last-4',
  display_name: 'display name',
};

/**
 * User Lookup panel (SSA-818): search by display name, user id or the last 4
 * phone digits, then flag / unflag (SSA-811), adjust coins, or expand the fraud
 * detail (heuristics that fired, flag history, 7-day activity). Phone is always
 * masked by the backend.
 */
export function UserLookupPanel({ env }: { env: Env }) {
  const { results, searchType, loading, error, search, getDetail, setResults } = useUserLookup(env);
  const actions = useUserActions(env);
  const [query, setQuery] = useState('');
  const [flagTarget, setFlagTarget] = useState<string | null>(null);
  const [flagReason, setFlagReason] = useState('');
  const [adjustUser, setAdjustUser] = useState<UserGamification | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [detailLoading, setDetailLoading] = useState<Set<string>>(new Set());
  const [historyOpen, setHistoryOpen] = useState<Set<string>>(new Set());

  const runSearch = () => {
    setNotice(null);
    setExpanded(new Set());
    setHistoryOpen(new Set());
    const q = query.trim();
    // The backend routes uuid / 4-digit / name itself; only guard the too-short case.
    if (q.length < MIN_SEARCH_LEN) return;
    search(q);
  };

  const patchUser = (userId: string, patch: Partial<UserGamification>) =>
    setResults(results.map((u) => (u.user_id === userId ? { ...u, ...patch } : u)));

  const withMember = (set: Set<string>, id: string, present: boolean) => {
    const next = new Set(set);
    if (present) next.add(id);
    else next.delete(id);
    return next;
  };

  /**
   * Expanding loads the detail once — a name / phone search returns summaries,
   * so flag history and activity have to be fetched per user.
   */
  const toggleDetail = async (user: UserGamification) => {
    const isOpen = expanded.has(user.user_id);
    setExpanded((prev) => withMember(prev, user.user_id, !isOpen));
    if (isOpen || user.flag_history) return;

    setDetailLoading((prev) => withMember(prev, user.user_id, true));
    const detail = await getDetail(user.user_id);
    setDetailLoading((prev) => withMember(prev, user.user_id, false));
    if (detail) patchUser(user.user_id, detail);
  };

  const doFlag = async (userId: string) => {
    if (flagReason.trim().length < MIN_FLAG_REASON) return;
    const ok = await actions.flagUser(userId, flagReason.trim());
    if (ok) {
      patchUser(userId, { is_flagged: true, flagged_reason: flagReason.trim() });
      setFlagTarget(null);
      setFlagReason('');
      setNotice('User flagged. Earning paths now blocked.');
    }
  };

  const doUnflag = async (userId: string) => {
    const ok = await actions.unflagUser(userId);
    if (ok) {
      patchUser(userId, { is_flagged: false, flagged_reason: null });
      setNotice('User unflagged.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && runSearch()}
          placeholder="Display name, full user id, or last 4 phone digits"
          className="flex-1 px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
        />
        <button
          onClick={runSearch}
          disabled={loading || query.trim().length < MIN_SEARCH_LEN}
          className="px-4 py-2 text-sm font-medium rounded-lg bg-violet-600 text-white hover:bg-violet-500 disabled:opacity-40"
        >
          Search
        </button>
      </div>

      {notice && <SuccessBanner message={notice} />}
      {error && <ErrorBanner message={error} />}
      {actions.error && <ErrorBanner message={actions.error} />}
      {loading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {!loading && results.length === 0 && (
        <p className="text-sm text-gray-500 py-6 text-center">
          No results. Search by display name, user id, or last-4 digits.
        </p>
      )}

      {!loading && results.length > 0 && searchType && (
        <p className="text-xs text-gray-500">
          {results.length} match{results.length === 1 ? '' : 'es'} by {SEARCH_TYPE_LABELS[searchType] ?? searchType}
        </p>
      )}

      <div className="space-y-3">
        {results.map((u) => (
          <div key={u.user_id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">{u.display_name || 'no display name'}</p>
                <p className="font-mono text-xs text-gray-400 mt-0.5">{u.user_id}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {u.phone_masked ?? 'no phone'} · {u.account_status ?? 'unknown'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-white tabular-nums">{u.wallet_balance.toLocaleString()}</p>
                <p className="text-xs text-gray-500">wallet · {u.total_earned.toLocaleString()} earned</p>
              </div>
            </div>

            {u.is_flagged && (
              <div className="mt-3 px-3 py-2 bg-red-950/40 border border-red-800/50 rounded-lg text-xs text-red-300">
                Flagged{u.flagged_reason ? `: ${u.flagged_reason}` : ''}
              </div>
            )}

            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={() => toggleDetail(u)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
              >
                {expanded.has(u.user_id) ? 'Hide fraud detail' : 'Fraud detail'}
              </button>
              <button
                onClick={() => setHistoryOpen((prev) => withMember(prev, u.user_id, !prev.has(u.user_id)))}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
              >
                {historyOpen.has(u.user_id) ? 'Hide transactions' : 'Transactions'}
              </button>
              <button
                onClick={() => setAdjustUser(u)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
              >
                Adjust coins
              </button>
              {u.is_flagged ? (
                <button
                  onClick={() => doUnflag(u.user_id)}
                  disabled={actions.loading}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-emerald-950/60 border border-emerald-800/50 text-emerald-300 hover:bg-emerald-900/50 disabled:opacity-40"
                >
                  Unflag
                </button>
              ) : (
                <button
                  onClick={() => {
                    setFlagTarget(u.user_id);
                    setFlagReason('');
                  }}
                  className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-950/60 border border-red-800/50 text-red-300 hover:bg-red-900/50"
                >
                  Flag for fraud
                </button>
              )}
            </div>

            {flagTarget === u.user_id && (
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <input
                  value={flagReason}
                  onChange={(e) => setFlagReason(e.target.value)}
                  placeholder={`Reason (min ${MIN_FLAG_REASON} chars)`}
                  className="flex-1 px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => doFlag(u.user_id)}
                    disabled={actions.loading || flagReason.trim().length < MIN_FLAG_REASON}
                    className="px-3 py-2 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-500 disabled:opacity-40"
                  >
                    Confirm flag
                  </button>
                  <button
                    onClick={() => setFlagTarget(null)}
                    className="px-3 py-2 text-xs text-gray-400 hover:text-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {expanded.has(u.user_id) && <FraudDetail user={u} loading={detailLoading.has(u.user_id)} />}

            {historyOpen.has(u.user_id) && (
              <TransactionHistory
                env={env}
                userId={u.user_id}
                onBalanceChange={(bal) => patchUser(u.user_id, { wallet_balance: bal })}
                onNotice={setNotice}
              />
            )}
          </div>
        ))}
      </div>

      {adjustUser && (
        <CoinAdjustModal
          env={env}
          user={adjustUser}
          onClose={() => setAdjustUser(null)}
          onAdjusted={(bal) => {
            patchUser(adjustUser.user_id, { wallet_balance: bal });
            setNotice('Coin adjustment applied.');
          }}
        />
      )}
    </div>
  );
}
